import { useContext, useEffect } from 'react';
import { useIntl } from 'gatsby-plugin-react-intl';
import { useFela } from 'react-fela';
import type CookieConsentNS from 'vanilla-cookieconsent';

import type { Theme } from '~styles/theme';
import { updateCampaignCookies } from '~utils/utm';

import { ConsentContext } from '../../context/ConsentContext';
import { useCookieConsentConfig } from '../../hooks/useCookieConsentConfig';
import { getInitialUrlSearch } from '../../utils/captureInitialUrlSearch';
import { mapLocaleToCcLang } from '../../utils/localeMap';
import { updateGtagConsent } from '../../utils/updateGtagConsent';

import { buildCookieConsentTheme } from './CookieConsent.rules';

interface CookieConsentEvent {
    cookie: CookieConsentNS.CookieValue;
}

const THEME_STYLE_ID = 'passwd-cookie-consent-theme';
const PREFERENCES_LINK_SELECTOR = '#cc-main .pm a';

let currentCcLang: string | null = null;

export const CookieConsent = () => {
    const intl = useIntl();
    const { theme } = useFela<Theme>();
    const config = useCookieConsentConfig();
    const ccLang = mapLocaleToCcLang(intl.locale);
    const ctx = useContext(ConsentContext);

    if (!ctx) {
        throw new Error('CookieConsent must be used within a ConsentProvider');
    }

    const { setConsent, setReady, registerOpenPreferences } = ctx;

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        let styleEl = document.getElementById(THEME_STYLE_ID) as HTMLStyleElement | null;

        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = THEME_STYLE_ID;
            document.head.appendChild(styleEl);
        }

        styleEl.textContent = buildCookieConsentTheme(theme);
    }, [theme]);

    useEffect(() => {
        let cancelled = false;
        let hidePreferencesOnLinkClick: ((event: MouseEvent) => void) | null = null;

        const handleConsent = (categories: readonly string[]) => {
            const marketing = categories.includes('marketing');

            setConsent({
                necessary: true,
                functional: categories.includes('functional'),
                analytics: categories.includes('analytics'),
                marketing,
            });
            updateGtagConsent(categories);

            if (marketing) {
                updateCampaignCookies(getInitialUrlSearch());
            }
        };

        const init = async () => {
            const cc = await import('vanilla-cookieconsent');

            await import('vanilla-cookieconsent/dist/cookieconsent.css');

            if (cancelled) {
                return;
            }

            // A link in the preferences dialog can point into the current page (the Cookie Policy anchor);
            // close the dialog so the visitor sees where it scrolled.
            hidePreferencesOnLinkClick = (event: MouseEvent) => {
                if (event.target instanceof Element && event.target.closest(PREFERENCES_LINK_SELECTOR)) {
                    cc.hidePreferences();
                }
            };
            document.addEventListener('click', hidePreferencesOnLinkClick);

            if (currentCcLang === null) {
                await cc.run({
                    ...config,
                    onFirstConsent: ({ cookie }: CookieConsentEvent) => handleConsent(cookie.categories),
                    onConsent: ({ cookie }: CookieConsentEvent) => handleConsent(cookie.categories),
                    onChange: ({ cookie }: CookieConsentEvent) => handleConsent(cookie.categories),
                });
                currentCcLang = ccLang;
            } else if (currentCcLang !== ccLang) {
                await cc.setLanguage(ccLang, true);
                currentCcLang = ccLang;
            }

            registerOpenPreferences(() => cc.showPreferences());
            setReady(true);
        };

        void init();

        return () => {
            cancelled = true;

            if (hidePreferencesOnLinkClick) {
                document.removeEventListener('click', hidePreferencesOnLinkClick);
            }
        };
    }, [ccLang, config, setConsent, setReady, registerOpenPreferences]);

    return null;
};
